// frontend/src/hooks/useMetaCubeOracleVisualizer.js
import { useState, useEffect, useRef } from "react";

/**
 * Hook MetaCube Oracle Visualizer
 * Gère cube central, surfaces flottantes et entropie dynamique (playlist).
 */
export default function useMetaCubeOracleVisualizer({
  playlistInterval = 15000,
  entropyInterval = 5000, // ⏳ rafraîchissement entropie backend
}) {
  const [frame, setFrame] = useState(null);
  const [entropy, setEntropy] = useState({ shannon: 0, accumulator: 0 });
  const [playlistIndex, setPlaylistIndex] = useState(0);
  const timerRef = useRef(null);

  // --- Récupération régulière des frames depuis backend ---
  useEffect(() => {
    let active = true;

    async function fetchFrame() {
      try {
        const res = await fetch("/api/geometry/metacube_oracle/animate");
        const data = await res.json();

        if (active && data.frames && data.frames.length > 0) {
          // Sélectionne le frame correspondant à l’index playlist
          const current = data.frames[playlistIndex % data.frames.length];
          setFrame(current);

          // Mets à jour entropie locale si dispo dans le frame
          setEntropy((prev) => ({
            shannon: current.shannon_entropy || prev.shannon,
            accumulator: current.entropy_accumulator || prev.accumulator,
          }));
        }
      } catch (err) {
        console.error("Erreur fetch frame MetaCubeOracle:", err);
      }
    }

    fetchFrame();
    const interval = setInterval(fetchFrame, 1000); // ~1 fps
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [playlistIndex]);

  // --- Playlist automatique ---
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setPlaylistIndex((prev) => prev + 1);
    }, playlistInterval);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [playlistInterval]);

  // --- Récupération Entropie Backend (/entropy) ---
  useEffect(() => {
    let active = true;

    async function fetchEntropy() {
      try {
        const res = await fetch("/api/geometry/metacube_oracle/entropy");
        const data = await res.json();

        if (active && typeof data.shannon_entropy === "number") {
          setEntropy({
            shannon: data.shannon_entropy,
            accumulator: data.entropy_accumulator || 0,
          });
        }
      } catch (err) {
        console.error("Erreur fetch entropy MetaCubeOracle:", err);
      }
    }

    fetchEntropy();
    const interval = setInterval(fetchEntropy, entropyInterval);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [entropyInterval]);

  return {
    frame,           // Données du frame courant
    entropy,         // { shannon, accumulator }
    playlistIndex,   // Index de la version en cours
    setPlaylistIndex // Permet de changer manuellement
  };
}
